import { Section, RelatedLinks, FinalCTA } from "@/components/v2/blocks";
import PageHeroPhoto from "@/components/v2/PageHeroPhoto";
import AnticiperTimeline from "@/components/v2/AnticiperTimeline";
import { Card, CardContent } from "@/components/ui/card";
import { Eye, Clock, ListChecks, HeartHandshake, FileText, CalendarClock, PenLine, Users, BarChart3 } from "lucide-react";

const ACCOMPAGNEMENT = [
  { icon: FileText, title: "Analyse du contrat actuel", text: "Nous étudions vos conditions et votre consommation." },
  { icon: CalendarClock, title: "Identification de l'échéance", text: "Nous repérons la fenêtre idéale pour agir." },
  { icon: PenLine, title: "Signature éventuelle de l'ACD", text: "Un accord qui nous autorise à consulter le marché pour vous." },
  { icon: Users, title: "Consultation fournisseurs", text: "Nous mettons les fournisseurs en concurrence." },
  { icon: BarChart3, title: "Présentation des résultats", text: "Nous vous expliquons les offres de façon claire." },
  { icon: HeartHandshake, title: "Accompagnement", text: "Nous vous guidons jusqu'à la décision finale." },
];

const ATOUTS = [
  { icon: Eye, title: "Une meilleure visibilité", text: "Vous gardez une vue claire sur vos échéances et vos marges de manœuvre." },
  { icon: Clock, title: "Plus de temps pour analyser", text: "Le temps d'étudier sereinement les solutions adaptées à votre profil." },
  { icon: ListChecks, title: "Une consultation structurée", text: "Une mise en concurrence des fournisseurs organisée et méthodique." },
  { icon: HeartHandshake, title: "Une décision plus sereine", text: "Vous choisissez au bon moment, sans pression et en confiance." },
];

export default function RenegociationContrats() {
  return (
    <div>
      <PageHeroPhoto
        cmsKey="renego"
        eyebrow="Professionnels · Anticipation"
        title="Anticipez le renouvellement de vos contrats d'énergie"
        subtitle="Les échéances contractuelles sont des moments clés dans la gestion énergétique d'une entreprise. Une préparation anticipée permet d'aborder ces échéances dans de meilleures conditions."
        image="/img/Anticipez.png"
        overlay="brand-light"
        flipLogo
        highlightsLabel="Pourquoi anticiper ?"
        highlights={[
          "Une meilleure visibilité sur vos échéances",
          "Plus de temps pour analyser les solutions",
          "Une consultation fournisseurs structurée",
          "Une prise de décision plus sereine",
        ]}
      />

      <AnticiperTimeline />

      <Section cmsKey="renego.pourquoi" eyebrow="Pourquoi anticiper ?" title="Le temps est votre meilleur allié" intro="Un contrat peut être signé jusqu'à deux ans avant son échéance. Anticiper, c'est choisir son moment plutôt que de le subir.">
        <div className="grid items-start gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {ATOUTS.map(({ icon: Icon, title, text }) => (
            <div
              key={title}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all duration-500 ease-out hover:-translate-y-3 hover:scale-[1.05] hover:border-primary hover:bg-primary hover:shadow-2xl hover:shadow-primary/40"
            >
              <span
                aria-hidden
                className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                style={{ background: "radial-gradient(130% 90% at 50% 0%, rgba(196,240,66,0.30), transparent 62%)" }}
              />
              <div className="relative">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm transition-all duration-500 group-hover:scale-110 group-hover:bg-accent group-hover:text-accent-foreground group-hover:shadow-lg group-hover:shadow-accent/40">
                  <Icon className="h-6 w-6" strokeWidth={2.1} aria-hidden />
                </div>
                <h3 className="font-display text-base font-bold text-primary mt-5 leading-snug transition-colors duration-500 group-hover:text-white">{title}</h3>
                <div className="grid grid-rows-[0fr] opacity-0 transition-all duration-500 ease-out group-hover:mt-2 group-hover:grid-rows-[1fr] group-hover:opacity-100">
                  <p className="overflow-hidden text-sm leading-relaxed text-white/85">{text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section cmsKey="renego.accompagnement" eyebrow="Notre accompagnement" title="Comment Energy Market vous accompagne" intro="Six étapes claires, de l'analyse de votre contrat jusqu'à la décision finale." muted>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {ACCOMPAGNEMENT.map(({ icon: Icon, title, text }, i) => (
            <Card key={title} className="relative overflow-hidden rounded-2xl border-border bg-card em-card-hover">
              <CardContent className="p-6">
                <span className="pointer-events-none absolute right-3 top-1 font-display text-6xl font-black text-primary/[0.06]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-6 w-6" strokeWidth={2.1} aria-hidden />
                </div>
                <h3 className="relative font-display text-base font-bold text-primary mt-5 leading-snug">{title}</h3>
                <p className="relative text-muted-foreground mt-2 text-sm leading-relaxed">{text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Pour aller plus loin">
        <RelatedLinks links={[
          { to: "/professionnels/optimisation-des-contrats", label: "Optimisation des contrats" },
          { to: "/professionnels/gestion-multi-sites", label: "Gestion multi-sites" },
          { to: "/contact", label: "Contact" },
        ]} />
      </Section>

      <FinalCTA title="Préparez votre échéance dès maintenant" text="Contactez Energy Market bien avant votre échéance : nous analysons votre situation et signons dès que le marché est favorable." />
    </div>
  );
}
